import { type FC, useState, useContext, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import classnames from 'classnames';
import { Video } from 'dibs-elements/exports/Video';
import { type ControlHandlers } from 'dibs-elements/exports/Video';
import { useLazyLoadMedia } from 'dibs-elements/exports/useLazyLoadMedia';
import { ioParams } from 'dibs-fastly/exports/imageOptimizations';

import { NftModelViewer } from '../../components/NftModelViewer';
import { NftPdpZoomModalLazy } from '../NftPdpZoomModal/NftPdpZoomModalLazy';
import { NftPdpHtmlViewer } from '../NftPdpHtmlViewer/NftPdpHtmlViewer';
import { ServerVarsContext } from '../../utils/ServerVarsContext';
import { type NftPdpMedia_nftItem$key } from './__generated__/NftPdpMedia_nftItem.graphql';

import styles from './NftPdpMedia.scss';
import layoutStyles from '../NftPdpRespLayout.scss';

type Props = {
    nftItem: NftPdpMedia_nftItem$key;
};

export type Media = 'video' | 'image' | '3d' | 'gif' | 'html';

const MAX_DESKTOP_SIZE = '714';

const imgParams = ioParams({
    height: MAX_DESKTOP_SIZE,
    width: MAX_DESKTOP_SIZE,
    fit: 'bounds',
    'resize-filter': 'bilinear',
    format: 'pjpg',
    disable: 'upscale',
});

const staticImgString = ioParams(imgParams, { dpr: '2', auto: 'webp', optimize: 'high' }).toQuery();

const imgPosterString = ioParams(imgParams, { auto: 'webp', optimize: 'high' }).toQuery();

const gifPosterString = ioParams(imgParams, { frame: '1' }).toQuery();

const gifToMp4String = ioParams({ format: 'mp4' }).toQuery();

function getMediaType(mimeType: string | undefined | null): Media | null {
    if (mimeType?.includes('gif')) return 'gif';
    if (mimeType?.includes('image')) return 'image';
    if (mimeType?.includes('video')) return 'video';
    if (mimeType?.includes('model')) return '3d';
    if (mimeType?.includes('html')) return 'html';
    return null;
}

function mediaTrackerFactory(action: string): () => void {
    return () =>
        trackEvent({
            event: 'e_ga_event',
            category: 'product',
            action,
            noninteraction: 0,
        });
}
const controlTracking: ControlHandlers = {
    onFullScreenOpen: mediaTrackerFactory('zoom in'),
    onFullScreenClose: mediaTrackerFactory('zoom out'),
    onPause: mediaTrackerFactory('pause'),
    onPlay: mediaTrackerFactory('play'),
    onReplay: mediaTrackerFactory('replay'),
    onMute: mediaTrackerFactory('mute'),
    onUnmute: mediaTrackerFactory('unmute'),
};

export const NftPdpMedia: FC<Props> = ({ nftItem: nftItemRef }) => {
    const nftItem = useFragment(
        graphql`
            fragment NftPdpMedia_nftItem on NftItem {
                serviceId
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                        aspectRatio
                        media {
                            mimeType
                            webUrl
                            dimensions
                            posterFrameUrl
                        }
                    }
                }
            }
        `,
        nftItemRef
    );
    const serverContext = useContext(ServerVarsContext);
    const isMobile = !!serverContext.isMobile;
    const imgRef = useRef<HTMLImageElement | null>(null);
    const { name, media, aspectRatio } = nftItem?.metadata || {};
    const { mimeType, webUrl, dimensions, posterFrameUrl } = media || {};
    const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
    const [isZoomModalLoaded, setIsZoomModalLoaded] = useState(false);
    const handleZoomModalOpen = (): void => {
        if (isZoomModalLoaded) {
            controlTracking.onFullScreenOpen?.();
            setIsZoomModalOpen(true);
        }
    };
    const type = getMediaType(mimeType);
    const [width, height] =
        // TODO: rethink how we handle variable dims, currently they'll always be square on the PDP
        // so just defaulting to 1000x1000
        dimensions === 'variable' ? ['1000', '1000'] : dimensions?.split('x') || [];
    const lazyLoad = useLazyLoadMedia('image', {
        ref: imgRef,
        width,
        height,
        shimmer: false,
    });

    if (type === 'image') {
        return (
            <div className={classnames(styles.container, layoutStyles.media)}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                <img
                    className={classnames(lazyLoad.styleProps.className, {
                        'is-zoom': isZoomModalLoaded,
                    })}
                    style={lazyLoad.styleProps.style}
                    width={width}
                    height={height}
                    src={webUrl + staticImgString}
                    srcSet={undefined /* TODO */}
                    alt={name || ''}
                    onClick={handleZoomModalOpen}
                    ref={imgRef}
                    loading="eager"
                />
                {webUrl && (
                    <NftPdpZoomModalLazy
                        src={webUrl}
                        type={type}
                        isMobile={!!isMobile}
                        isOpen={isZoomModalOpen}
                        onMount={() => setIsZoomModalLoaded(true)}
                        onClose={() => {
                            setIsZoomModalOpen(false);
                            controlTracking.onFullScreenClose?.();
                        }}
                        staticImg={
                            <img
                                className={styles.staticImage}
                                width={width}
                                height={height}
                                src={webUrl + staticImgString}
                                alt={name || ''}
                                loading="eager"
                            />
                        }
                    />
                )}
            </div>
        );
    } else if (type === 'video') {
        const poster = posterFrameUrl + imgPosterString;
        return (
            <Video
                className={layoutStyles.media}
                width={width}
                height={height}
                placeholder="spinner"
                poster={poster}
                keyboardShortcuts
                preload="auto"
                controlsList="play replay audio darkmode fullscreen"
                controlHandlers={controlTracking}
            >
                <source src={webUrl ?? undefined} type={mimeType ?? undefined} />
            </Video>
        );
    } else if (type === 'gif') {
        const poster = posterFrameUrl ? posterFrameUrl + imgPosterString : webUrl + gifPosterString;
        return (
            <Video
                className={layoutStyles.media}
                width={width}
                height={height}
                placeholder="spinner"
                poster={poster || undefined}
                controlsList="darkmode fullscreen"
                preload="auto"
                controlHandlers={controlTracking}
            >
                <source src={webUrl + gifToMp4String} type="video/mp4" />
            </Video>
        );
    } else if (type === '3d') {
        const poster = posterFrameUrl + imgPosterString;
        return (
            <NftModelViewer
                src={webUrl ?? ''}
                height={height}
                width={width}
                posterFrameUrl={poster}
                onFullScreenOpen={controlTracking.onFullScreenOpen}
                onFullScreenClose={controlTracking.onFullScreenClose}
                constainerClass={layoutStyles.media}
                controlsList="fullscreen"
            />
        );
    } else if (type === 'html') {
        const src = webUrl ? `${webUrl}?1stdibsId=${nftItem.serviceId}` : '';
        const poster = posterFrameUrl + imgPosterString;
        return (
            <NftPdpHtmlViewer
                src={src}
                poster={poster}
                isMobile={!!isMobile}
                aspectRatio={aspectRatio}
            />
        );
    } else {
        return null;
    }
};
